import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The four distributions worth recognising on sight.
 *
 * A named distribution is not a formula to memorise — it is a *set of
 * conditions*, and the whole skill of this unit is reading a situation and
 * deciding which set it satisfies. So every lesson here opens with the
 * conditions and closes with a table of situations to classify, and the three
 * discrete lessons are built to be confusable in exactly the ways students
 * confuse them:
 *
 * - binomial and geometric share their trials and differ in what is counted —
 *   successes in a fixed number of tries, against tries until the first success;
 * - Poisson and binomial share their counting and differ in whether there is an
 *   n at all — which is why the Poisson lesson opens on a situation where "out
 *   of how many?" has no answer.
 *
 * `normal-random-variable` finishes the unit and is the one that goes beyond
 * Grade 11: the empirical rule answers questions about 1, 2 and 3 standard
 * deviations, and here z = 1.37 is as answerable as z = 2, the question can run
 * backwards from a percentile to a value, and a binomial with enough trials can
 * borrow the curve.
 */
export const DISTRIBUTION_LESSONS: Lesson[] = [
  lesson({
    id: 'binomial-distribution-01',
    unitId: 'unit-distributions',
    conceptId: 'binomial-distribution',
    title: 'The Binomial Distribution',
    summary: 'Counting successes in a fixed number of independent tries — the distribution that four conditions produce.',
    objectives: [
      'Check the four conditions before naming a situation binomial',
      'Compute P(X = k) and cumulative binomial probabilities',
      'Use E(X) = np and Var(X) = np(1 − p)',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The same problem, over and over',
        blocks: [
          text('Twelve patients take a drug that works 70% of the time. Twenty components come off a line that is 3% defective. Fifty people are polled in a country where 46% support a measure.'),
          text('Three unrelated stories, one piece of mathematics. Each fixes a number of independent tries in advance, each try either works or does not with the same probability, and each asks how many of them worked.'),
          insight('That shape has a name — the **binomial distribution** — and recognising it is worth more than the formula, because the formula is useless applied to a situation that fails one of the conditions.'),
          math('P(X = k) = {}^{n}C_{k}\\;p^{k}(1 - p)^{n-k}'),
          text('Three factors, each doing an identifiable job: how many arrangements have k successes, the probability of the k successes, and the probability of the n − k failures.'),
        ],
      },
      intuition: {
        title: 'Four conditions, then three factors',
        blocks: [
          text('A situation is binomial when **all four** of these hold. They are worth checking in order, because the last two are the ones that fail:'),
          list(
            [
              '**Fixed n** — the number of trials is decided before you start.',
              '**Two outcomes** — each trial is a success or a failure, with nothing in between.',
              '**Constant p** — the probability of success is the same on every trial.',
              '**Independence** — no trial affects any other.',
            ],
            true,
          ),
          warning('Sampling without replacement from a small population breaks the last two at once: each draw changes what is left, so p moves and the trials are dependent. Drawing 5 cards from 52 is *not* binomial. Drawing 5 people from a city of a million is not either, strictly — but the change is in the sixth decimal place, and treating it as binomial is a deliberate and standard approximation.', 'Where the conditions actually fail'),
          text('Now the formula, factor by factor. To get exactly k successes in n trials:'),
          table(
            ['Factor', 'What it counts', 'Why it is there'],
            [
              ['ⁿCₖ', 'the arrangements', 'HHT, HTH and THH are three different ways to get 2 heads in 3'],
              ['pᵏ', 'the successes', 'k independent successes, so the probabilities multiply'],
              ['(1 − p)ⁿ⁻ᵏ', 'the failures', 'the other trials must all fail — this is not optional'],
            ],
          ),
          warning('Dropping ⁿCₖ gives the probability of **one specific sequence**, not of k successes. It is the most common error in the topic and it always makes the answer too small — often by a factor of hundreds.', 'The coefficient is not decoration'),
          text('The mean and variance need no summation, and both are worth being able to justify:'),
          math('E(X) = np, \\qquad \\operatorname{Var}(X) = np(1-p), \\qquad \\sigma = \\sqrt{np(1-p)}'),
          insight('E(X) = np because X is the sum of n trials each with expectation p, and expectations of a sum always add. Var(X) = np(1 − p) for the same reason plus one extra condition — variances add only for *independent* variables, which is exactly what condition four supplied. The formulas are the previous lesson, applied.'),
          text('Cumulative questions are sums, and the phrasing decides which one:'),
          list([
            'P(X ≤ 3) = P(0) + P(1) + P(2) + P(3).',
            'P(X ≥ 4) = 1 − P(X ≤ 3) — the complement is nearly always shorter.',
            'P(X > 3) is the same as P(X ≥ 4) for a whole-number count, and *not* the same as P(X ≥ 3).',
          ]),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '0', value: 1 },
                { label: '1', value: 10 },
                { label: '2', value: 44 },
                { label: '3', value: 117 },
                { label: '4', value: 205 },
                { label: '5', value: 246 },
                { label: '6', value: 205 },
                { label: '7', value: 117 },
                { label: '8', value: 44 },
                { label: '9', value: 10 },
                { label: '10', value: 1 },
              ],
              unitLabel: 'n = 10, p = 0.5 — probability × 1000',
            },
            'Symmetric when p = 0.5, and centred on np = 5. The bars are the row of Pascal’s triangle, scaled — which is what the ⁿCₖ factor is.',
          ),
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '0', value: 107 },
                { label: '1', value: 268 },
                { label: '2', value: 302 },
                { label: '3', value: 201 },
                { label: '4', value: 88 },
                { label: '5', value: 26 },
                { label: '6', value: 6 },
                { label: '7', value: 1 },
              ],
              unitLabel: 'n = 10, p = 0.2 — probability × 1000',
            },
            'The same n with p = 0.2. The peak moves to np = 2 and the shape leans right, because there is far more room above the mean than below it. Bars for 8, 9 and 10 exist but round to nothing.',
          ),
          tip('Sketch where np falls before computing. An answer that puts most of the probability far from np is almost always a dropped coefficient or a swapped p and 1 − p.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A component is defective with probability 0.05, independently. A box holds 20. Find the probability that exactly 2 are defective, that at most 1 is, and give the mean and standard deviation of the number of defectives.'),
          steps([
            { label: 'Check the conditions.', latex: 'n = 20,\\; p = 0.05', note: 'Fixed n, two outcomes, constant p, independent — binomial.' },
            { label: 'Exactly 2: coefficient, successes, failures.', latex: 'P(X = 2) = {}^{20}C_{2}(0.05)^{2}(0.95)^{18}' },
            { label: 'Evaluate.', latex: '190 \\times 0.0025 \\times 0.3972 \\approx 0.189' },
            { label: 'At most 1 is two terms.', latex: 'P(X \\le 1) = (0.95)^{20} + 20(0.05)(0.95)^{19}' },
            { label: 'Evaluate.', latex: '0.3585 + 0.3774 \\approx 0.736' },
            { label: 'Mean and variance from the formulas.', latex: 'E(X) = 20(0.05) = 1, \\quad \\operatorname{Var}(X) = 20(0.05)(0.95) = 0.95' },
            { label: 'Standard deviation.', latex: '\\sigma = \\sqrt{0.95} \\approx 0.97' },
          ]),
          text('Step 3 is where the coefficient proves itself. Without the 190, the answer would be 0.00099 — the probability that the *first two* components are the defective ones and the other eighteen are fine. That is a real probability, of a much more specific event than the question asked about.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('binomial-distribution', 4)] },
      independent: { title: 'On your own', blocks: [practice('binomial-distribution', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('binomial-distribution', 3, 'Cumulative probabilities, and situations that fail one of the four conditions.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Binomial needs all four: fixed n, two outcomes, constant p, independent trials.',
            'P(X = k) = ⁿCₖ pᵏ(1 − p)ⁿ⁻ᵏ — arrangements, successes, failures.',
            'Omitting ⁿCₖ answers a question about one particular sequence.',
            'E(X) = np and Var(X) = np(1 − p); σ is the square root of the latter.',
            'Sampling without replacement from a small population is not binomial.',
            '"At least k" is usually best as 1 − P(X ≤ k − 1).',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'geometric-distribution-01',
    unitId: 'unit-distributions',
    conceptId: 'geometric-distribution',
    title: 'The Geometric Distribution',
    summary: 'The same trials, a different question: not how many succeed, but how long until one does.',
    objectives: [
      'Recognise a geometric situation and distinguish it from a binomial one',
      'Compute P(X = k) and P(X > k) for a geometric variable',
      'Use E(X) = 1/p and explain the memoryless property',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'How many tries?',
        blocks: [
          text('A basketball player scores 40% of their free throws. Two questions: how many of the next 10 will they score, and how many throws until the first one goes in?'),
          text('The first is binomial. The second is not — and it is not because the number of trials is not fixed. It could be 1, or 4, or 37; the experiment stops when the first success arrives, and stopping is the whole design.'),
          insight('The **geometric distribution** counts trials up to and including the first success. Same trials, same constant p, same independence — three of the four binomial conditions survive. What goes is the fixed n, and with it goes the coefficient.'),
          math('P(X = k) = (1 - p)^{k-1}\\,p'),
          text('One arrangement, not many: the first k − 1 trials must all fail and the kth must succeed. There is exactly one way for that to happen, which is why no ⁿCₖ appears.'),
        ],
      },
      intuition: {
        title: 'The tail, and the surprising mean',
        blocks: [
          text('Because the sequence is forced, the two most useful facts come out almost without work. To need **more than** k trials, the first k must all fail:'),
          math('P(X > k) = (1 - p)^{k}'),
          text('And its complement, which is how "within the first k tries" questions are answered:'),
          math('P(X \\le k) = 1 - (1 - p)^{k}'),
          insight('This is the "at least one success in k tries" calculation from the independence lesson, arrived at from the other direction. Succeeding within k tries and failing k times running are complementary descriptions of the same experiment.'),
          text('The mean is the memorable result of the topic:'),
          math('E(X) = \\frac{1}{p}'),
          text('A 1-in-6 event takes 6 tries on average. A 1-in-1000 event takes 1000. The number is exactly what intuition suggests, which is unusual enough in probability to be worth noticing — and it is a genuine theorem, not a definition.'),
          warning('E(X) = 1/p is an average, not a guarantee or even a likely value. With p = 1/6 the *most likely* number of trials is 1, with probability 1/6 — the mode is always 1 for a geometric distribution, however small p is. The mean of 6 is pulled up by a long tail of unlucky runs.', 'The mean is not the most likely value'),
          text('And the property that gives the distribution its reputation:'),
          math('P(X > m + k \\mid X > m) = P(X > k)'),
          insight('**Memorylessness**. Ten failures in a row do not bring you closer to a success: the eleventh trial has probability p, exactly as the first did. The distribution of *remaining* trials is the same distribution you started with. This is the gambler’s fallacy stated as a theorem and refuted by it.'),
          text('The two distributions side by side, since telling them apart is the skill:'),
          table(
            ['', 'Binomial', 'Geometric'],
            [
              ['The question', 'how many successes in n?', 'how many trials until the first?'],
              ['n', 'fixed in advance', 'not fixed — that is the answer'],
              ['Values of X', '0, 1, …, n', '1, 2, 3, … with no upper limit'],
              ['P(X = k)', 'ⁿCₖ pᵏ(1−p)ⁿ⁻ᵏ', '(1−p)ᵏ⁻¹p'],
              ['Mean', 'np', '1/p'],
            ],
          ),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '1', value: 300 },
                { label: '2', value: 210 },
                { label: '3', value: 147 },
                { label: '4', value: 103 },
                { label: '5', value: 72 },
                { label: '6', value: 50 },
                { label: '7', value: 35 },
                { label: '8', value: 25 },
                { label: '9', value: 17 },
                { label: '10', value: 12 },
              ],
              unitLabel: 'p = 0.3 — probability × 1000, first ten values',
            },
            'Always decreasing, always. Each bar is 0.7 of the one before, which is what "one more failure first" costs. X = 1 is the most likely outcome even though the mean is 1/0.3 ≈ 3.3.',
          ),
          text('The tail is where the mean comes from. The bars above account for 97% of the probability; the remaining 3% is spread over every value from 11 upwards, and includes runs of thirty and forty failures that pull the average up above the visible hump.'),
          table(
            ['p', 'Most likely X', 'E(X)', 'P(X > 10)'],
            [
              ['0.5', '1', '2', '0.001'],
              ['0.3', '1', '3.33', '0.028'],
              ['0.1', '1', '10', '0.349'],
              ['0.02', '1', '50', '0.817'],
            ],
          ),
          tip('The mode is 1 in every row. Any answer claiming the most likely wait is near 1/p has confused the mean with the mode.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A machine jams on 8% of cycles, independently. Find the probability that the first jam is on the 5th cycle, that no jam occurs in the first 10 cycles, and the expected cycle of the first jam. Then: given 10 clean cycles, what is the probability of no jam in the next 10?'),
          steps([
            { label: 'Geometric with p = 0.08. First jam on the 5th: four clean, then a jam.', latex: 'P(X = 5) = (0.92)^{4}(0.08) \\approx 0.0573' },
            { label: 'No jam in 10 cycles is P(X > 10).', latex: '(0.92)^{10} \\approx 0.4344' },
            { label: 'The expected wait.', latex: 'E(X) = \\tfrac{1}{0.08} = 12.5\\text{ cycles}' },
            { label: 'The conditional question — memorylessness.', latex: 'P(X > 20 \\mid X > 10) = P(X > 10) \\approx 0.4344', note: 'Identical to step 2. The machine has no memory of its clean run.' },
          ]),
          text('Step 4 is the one people refuse to believe, and the arithmetic settles it: P(X > 20)/P(X > 10) = 0.92²⁰/0.92¹⁰ = 0.92¹⁰. The ten clean cycles cancel exactly, leaving the original probability untouched.'),
          warning('It is worth being precise about what memorylessness does *not* say. It does not say a long clean run is unremarkable — 0.92¹⁰ is under a half, so ten clean cycles is mildly lucky. It says only that the luck does not carry forward.', 'No debt, and no credit'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('geometric-distribution', 4)] },
      independent: { title: 'On your own', blocks: [practice('geometric-distribution', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('geometric-distribution', 3, 'Tails, conditional waits, and telling geometric from binomial.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Geometric counts trials up to and including the first success; n is not fixed.',
            'P(X = k) = (1 − p)ᵏ⁻¹p — one forced sequence, so no binomial coefficient.',
            'P(X > k) = (1 − p)ᵏ: the first k trials all failed.',
            'E(X) = 1/p, but the most likely value is always 1.',
            'The distribution is memoryless: past failures do not raise the next probability.',
            'Ask what the question counts — successes, or trials — to choose between binomial and geometric.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'poisson-distribution-01',
    unitId: 'unit-distributions',
    conceptId: 'poisson-distribution',
    title: 'The Poisson Distribution',
    summary: 'Counting events in an interval when there is no fixed number of trials to count out of.',
    objectives: [
      'Recognise a Poisson situation by the absence of a fixed n',
      'Compute Poisson probabilities and scale λ to a new interval',
      'Use the Poisson approximation to a binomial with large n and small p',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Out of how many?',
        blocks: [
          text('A call centre takes 6 calls an hour on average. What is the probability of exactly 4 in the next hour?'),
          text('Try to make that binomial. n is the number of trials — but what is a trial? There is no list of opportunities that either produced a call or did not. A call can arrive at any instant, and the question "6 out of how many?" has no answer.'),
          insight('The **Poisson distribution** is what counting looks like when there is no n. It needs only a rate: λ (lambda), the mean number of events per interval. That single number is the entire specification.'),
          math('P(X = k) = \\frac{e^{-\\lambda}\\lambda^{k}}{k!}'),
          text('Typographically unlike anything else in the course, and it is doing the same job as always: a probability for each whole-number count, from 0 upwards with no upper limit.'),
        ],
      },
      intuition: {
        title: 'What λ has to satisfy, and how it scales',
        blocks: [
          text('A count is Poisson when three things hold over the interval concerned:'),
          list(
            [
              '**Constant average rate** — events arrive at the same average rate throughout.',
              '**Independence** — one event does not make another more or less likely.',
              '**No simultaneity** — events happen one at a time, not in inseparable clumps.',
            ],
            true,
          ),
          warning('The rate condition is the one that fails in real data. Calls to a helpline are not Poisson across a whole day — they cluster at 9am and again at lunch. Poisson may be a fine model *within* an hour and a poor one across twelve.', 'A constant rate is a strong claim'),
          text('The distinguishing property, and the reason the formula has only one parameter:'),
          math('E(X) = \\lambda, \\qquad \\operatorname{Var}(X) = \\lambda'),
          insight('Mean and variance are the same number. No other distribution in this course does that, and it doubles as a diagnostic: given a data set whose mean and variance are wildly different, Poisson is the wrong model, whatever the story suggests.'),
          text('**Scaling** is where most exam marks are lost and gained. λ is tied to an interval, and changing the interval changes λ proportionally:'),
          table(
            ['Rate given', 'Interval asked about', 'λ to use'],
            [
              ['6 per hour', '1 hour', '6'],
              ['6 per hour', '30 minutes', '3'],
              ['6 per hour', '20 minutes', '2'],
              ['6 per hour', '3 hours', '18'],
              ['2 per metre', '4.5 metres', '9'],
            ],
          ),
          warning('Scale λ *before* substituting, never after. Computing the hourly probability and then halving it answers nothing — probabilities do not scale with time, rates do.', 'Scale the rate, not the answer'),
          text('Finally, the connection back to the binomial. Poisson is what a binomial becomes when the trials are many and each is unlikely:'),
          math('\\text{if } n \\ge 50 \\text{ and } p \\le 0.1, \\quad B(n, p) \\approx \\text{Poisson}(\\lambda = np)'),
          insight('This is why Poisson turns up for typos per page, faults per kilometre of cable and accidents per year. Each is really an enormous number of tiny-probability opportunities — every word, every centimetre, every journey — and nobody can say how many. Poisson replaces the unknowable n and the unknowable p by their product, which is a rate anyone can measure.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '0', value: 135 },
                { label: '1', value: 271 },
                { label: '2', value: 271 },
                { label: '3', value: 180 },
                { label: '4', value: 90 },
                { label: '5', value: 36 },
                { label: '6', value: 12 },
                { label: '7', value: 3 },
              ],
              unitLabel: 'λ = 2 — probability × 1000',
            },
            'A small λ leans hard to the right: with a mean of 2 there is no room to be far below and plenty to be far above. Note the tie at 1 and 2 — a whole-number λ always has two equally likely modes.',
          ),
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: '2', value: 22 },
                { label: '3', value: 52 },
                { label: '4', value: 91 },
                { label: '5', value: 128 },
                { label: '6', value: 146 },
                { label: '7', value: 149 },
                { label: '8', value: 130 },
                { label: '9', value: 101 },
                { label: '10', value: 71 },
                { label: '11', value: 45 },
                { label: '12', value: 26 },
                { label: '13', value: 14 },
              ],
              unitLabel: 'λ = 7 — probability × 1000',
            },
            'A larger λ straightens up and starts to look like a bell — which is not a coincidence, and is the fact the next lesson will use.',
          ),
          text('The approximation in one table. A binomial with n = 100 and p = 0.02, against Poisson with λ = 2:'),
          table(
            ['k', 'Binomial(100, 0.02)', 'Poisson(2)'],
            [
              ['0', '0.1326', '0.1353'],
              ['1', '0.2707', '0.2707'],
              ['2', '0.2734', '0.2707'],
              ['3', '0.1823', '0.1804'],
              ['4', '0.0902', '0.0902'],
            ],
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Faults occur in a cable at an average rate of 0.6 per kilometre, independently and at a constant rate. Find the probability of no faults in 5 km, of exactly 2 faults in 5 km, and of at least 2 faults in 5 km.'),
          steps([
            { label: 'Scale the rate to the interval asked about — first, before anything else.', latex: '\\lambda = 0.6 \\times 5 = 3' },
            { label: 'No faults: k = 0, and 0! = 1.', latex: 'P(X = 0) = e^{-3} \\approx 0.0498' },
            { label: 'Exactly 2.', latex: 'P(X = 2) = \\frac{e^{-3}3^{2}}{2!} = \\frac{0.0498 \\times 9}{2} \\approx 0.224' },
            { label: 'At least 2 — take the complement of "0 or 1".', latex: 'P(X = 1) = e^{-3}(3) \\approx 0.149' },
            { label: 'Subtract from 1.', latex: 'P(X \\ge 2) = 1 - (0.0498 + 0.149) \\approx 0.801' },
          ]),
          text('Step 1 is the whole problem. With λ = 0.6 the answers would describe one kilometre of cable and would be out by a factor of five in the exponent — P(X = 0) would come to 0.549 rather than 0.0498, which is the difference between "faults are rare" and "faults are near-certain over this length".'),
          tip('Write down λ and the interval it belongs to on the first line of every Poisson answer. Nearly every wrong answer in this topic is the right formula with the wrong λ.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('poisson-distribution', 4)] },
      independent: { title: 'On your own', blocks: [practice('poisson-distribution', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('poisson-distribution', 3, 'Rescaled intervals, cumulative counts, and the binomial approximation.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Poisson counts events in an interval when there is no fixed n to count out of.',
            'P(X = k) = e^(−λ)λᵏ ÷ k!, and λ is the mean count for the interval concerned.',
            'E(X) = Var(X) = λ — a property no other distribution here shares.',
            'Rescale λ in proportion to the interval, before substituting.',
            'A binomial with large n and small p is approximately Poisson with λ = np.',
            'The constant-rate assumption is the one real data most often breaks.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'normal-random-variable-01',
    unitId: 'unit-distributions',
    conceptId: 'normal-random-variable',
    title: 'Normal Random Variables',
    summary: 'Any area under the bell curve, the question run backwards, and the curve lent to a binomial.',
    objectives: [
      'Find P(X < a) for any a using standardisation and a z-table',
      'Run the calculation backwards from a proportion to a value',
      'Approximate a binomial by a normal, with the continuity correction',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Between the marks on the ruler',
        blocks: [
          text('Grade 11 gave you the empirical rule: 68%, 95%, 99.7% within one, two and three standard deviations. It answers a question about z = 1, z = 2 and z = 3, and it has nothing whatever to say about z = 1.37.'),
          text('Most real questions are about z = 1.37. What proportion of components are under 512 g? What mark is the top 15% cut-off? Neither lands on a whole standard deviation, and neither can be answered by a rule with three entries.'),
          insight('The fix is a **table** (or a calculator) holding the area to the left of every z, in steps of 0.01. The empirical rule is three rows of that table, remembered. Everything else is the same procedure with a lookup in the middle.'),
          math('Z = \\frac{X - \\mu}{\\sigma} \\sim N(0, 1)'),
          text('**Standardising** converts any normal variable into the one standard normal Z, which is the only distribution the table has to cover. That is the trick that makes a single table serve every normal question ever asked.'),
        ],
      },
      intuition: {
        title: 'Left, right, between — and backwards',
        blocks: [
          text('A z-table gives Φ(z) = P(Z < z), the area to the **left**. Every other question is that area rearranged:'),
          table(
            ['Question', 'In terms of Φ', 'Why'],
            [
              ['P(X < a)', 'Φ(z_a)', 'the table directly'],
              ['P(X > a)', '1 − Φ(z_a)', 'total area is 1'],
              ['P(a < X < b)', 'Φ(z_b) − Φ(z_a)', 'left of b, minus left of a'],
              ['P(X < a) for negative z', '1 − Φ(|z_a|)', 'symmetry, if the table has no negatives'],
            ],
          ),
          warning('Subtract in the right order. Φ(z_a) − Φ(z_b) with a < b gives a negative probability, which is the arithmetic telling you the subtraction was backwards. Larger z first, always.', 'Bigger minus smaller'),
          text('An **inverse** question runs the machine the other way: given a proportion, find the value. The steps reverse exactly.'),
          list(
            [
              'Sketch the curve and shade the region the question describes.',
              'Convert to an area *to the left*, since that is what the table is indexed by.',
              'Look the area up in the body of the table and read off the z.',
              'Un-standardise: x = μ + zσ.',
            ],
            true,
          ),
          insight('Step 4 is the standardising formula solved for x, and it says something worth saying aloud: a z-score is a set of instructions for finding a value — "start at the mean, go z standard deviations to the right". A negative z means go left.'),
          text('Finally, the connection that ties this unit together. A binomial with a large n looks like a normal curve, and may be replaced by one:'),
          math('\\text{if } np > 5 \\text{ and } n(1-p) > 5, \\quad B(n,p) \\approx N\\big(np,\\; np(1-p)\\big)'),
          warning('A binomial counts whole things and a normal curve is continuous, so the bar for X = 7 spans 6.5 to 7.5. Use those edges — the **continuity correction** — or a discrete P(X ≥ 7) becomes a continuous P(X > 7) and silently loses the whole of the seventh bar.', 'Half a unit, every time'),
          table(
            ['Discrete question', 'Continuous version'],
            [
              ['P(X = 7)', 'P(6.5 < X < 7.5)'],
              ['P(X ≥ 7)', 'P(X > 6.5)'],
              ['P(X > 7)', 'P(X > 7.5)'],
              ['P(X ≤ 7)', 'P(X < 7.5)'],
            ],
          ),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'normal-curve',
              mean: 500,
              sd: 8,
              shade: { from: -3.4, to: 1.5, label: 'Φ(1.5) = 0.9332' },
              unitLabel: 'fill weight (g)',
              label: 'P(X < 512)',
            },
            'The table always reports the area to the left, whatever the question asks. Here z = (512 − 500)/8 = 1.5 and the shaded region is 93.32% of the curve.',
          ),
          figure(
            {
              kind: 'normal-curve',
              mean: 500,
              sd: 8,
              shade: { from: 1.5, to: 3.4, label: '0.0668' },
              unitLabel: 'fill weight (g)',
              label: 'P(X > 512)',
            },
            'The same z, the other tail: 1 − 0.9332 = 0.0668. Sketching first is what stops the two being swapped, and swapping them is the most common error in the topic.',
          ),
          text('An inverse question, laid out as a table so the direction of travel is visible:'),
          table(
            ['Forwards (value → proportion)', 'Backwards (proportion → value)'],
            [
              ['start with x = 512', 'start with "the top 15%"'],
              ['z = (512 − 500)/8 = 1.5', 'area to the left = 0.85'],
              ['look up Φ(1.5) = 0.9332', 'search the body: z ≈ 1.04'],
              ['answer: 0.9332', 'x = 500 + 1.04(8) ≈ 508.3 g'],
            ],
          ),
          tip('Sketch before every one of these, forwards or backwards. The sketch decides whether you want an area or its complement, and the arithmetic cannot.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Jars are filled with mean 500 g and standard deviation 8 g, normally distributed. (a) What proportion are between 490 g and 512 g? (b) Below what weight are the lightest 5%? (c) A coin is tossed 100 times — approximate P(at least 60 heads).'),
          steps([
            { label: '(a) Standardise both ends.', latex: 'z_1 = \\frac{490 - 500}{8} = -1.25, \\quad z_2 = \\frac{512 - 500}{8} = 1.5' },
            { label: 'Look up both areas to the left, and subtract in the right order.', latex: '\\Phi(1.5) - \\Phi(-1.25) = 0.9332 - 0.1056 = 0.8276' },
            { label: '(b) The lightest 5% is an area of 0.05 on the left. Find that z.', latex: 'z \\approx -1.645' },
            { label: 'Un-standardise.', latex: 'x = 500 + (-1.645)(8) \\approx 486.8\\text{ g}' },
            { label: '(c) Check the binomial is large enough, and find its mean and σ.', latex: 'np = 50 > 5, \\quad \\sigma = \\sqrt{100(0.5)(0.5)} = 5' },
            { label: 'Apply the continuity correction: "at least 60" starts at 59.5.', latex: 'z = \\frac{59.5 - 50}{5} = 1.9' },
            { label: 'Read the upper tail.', latex: 'P \\approx 1 - \\Phi(1.9) = 1 - 0.9713 = 0.0287' },
          ]),
          text('Without the correction, step 6 would use 60 and give z = 2, for an answer of 0.0228 — a fifth too small, entirely because half of the sixtieth bar was thrown away. The exact binomial answer is 0.0284, so the corrected approximation is right to three decimal places and the uncorrected one is not right to two.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('normal-random-variable', 4)] },
      independent: { title: 'On your own', blocks: [practice('normal-random-variable', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('normal-random-variable', 3, 'Inverse problems, two-sided areas, and binomials borrowing the curve.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Standardise with z = (x − μ)/σ; the table then covers every normal distribution at once.',
            'Φ(z) is the area to the LEFT. Right tails are 1 − Φ(z); a band is the difference, larger minus smaller.',
            'Inverse questions go proportion → area on the left → z → x = μ + zσ.',
            'Sketch and shade before computing; the sketch decides which area you want.',
            'B(n, p) ≈ N(np, np(1 − p)) when np and n(1 − p) both exceed 5.',
            'Apply the continuity correction — half a unit outwards — whenever a whole-number count borrows a continuous curve.',
          ]),
        ],
      },
    },
  }),
];
